// Movimiento del panel.
//
// Una sola curva para todo —la de iOS, que arranca rapido y frena suave— y
// un solo lugar donde se decide cuanto dura cada cosa. Si cada tarjeta se
// anima a su manera, la pagina se siente hecha por tres personas distintas.
//
// Todo respeta "reducir movimiento" del sistema: quien lo tenga activado ve
// la pagina quieta y completa, no a medio dibujar.

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';

export const SUAVE = 'cubic-bezier(.22,1,.36,1)';

const sinMovimiento = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Entrada escalonada. `i` es el lugar en la fila: cada uno sale 70 ms despues. */
export function Aparece({ i = 0, children, sx = {} }) {
  return (
    <Box
      sx={{
        height: 1,
        '@keyframes entra': {
          from: { opacity: 0, transform: 'translateY(16px) scale(.985)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: `entra .6s ${SUAVE} both`,
        animationDelay: `${Math.min(i, 12) * 70}ms`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** La tarjeta se levanta un poco al pasar el mouse. Se usa con sx={{...levanta}}. */
export const levanta = {
  transition: `transform .35s ${SUAVE}, box-shadow .35s ${SUAVE}`,
  '&:hover': { transform: 'translateY(-3px)' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
};

/**
 * El numero sube desde cero hasta su valor.
 *
 * Es lo que hace que la cifra se lea como algo vivo y no como texto pegado.
 * Devuelve el valor en curso; el formato (soles, porcentaje) lo pone quien
 * lo usa, para que el conteo no invente separadores a mitad de camino.
 */
export function useConteo(valor, ms = 1000) {
  const destino = Number(valor) || 0;
  const [actual, setActual] = useState(() => (sinMovimiento() ? destino : 0));

  useEffect(() => {
    if (sinMovimiento()) {
      setActual(destino);
      return undefined;
    }

    let cuadro;
    const inicio = performance.now();
    const paso = (ahora) => {
      const avance = Math.min((ahora - inicio) / ms, 1);
      // Frena al final, igual que la curva de las tarjetas.
      setActual(destino * (1 - Math.pow(1 - avance, 4)));
      if (avance < 1) cuadro = requestAnimationFrame(paso);
    };
    cuadro = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(cuadro);
  }, [destino, ms]);

  return actual;
}

/** Barra vertical que crece desde el piso, escalonada. */
export const creceDesdeAbajo = (i = 0) => ({
  transformOrigin: 'bottom',
  '@keyframes creceY': { from: { transform: 'scaleY(0)' }, to: { transform: 'scaleY(1)' } },
  animation: `creceY .7s ${SUAVE} both`,
  animationDelay: `${260 + i * 80}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/** Barra horizontal que crece desde la izquierda, escalonada. */
export const creceDesdeIzquierda = (i = 0) => ({
  transformOrigin: 'left',
  '@keyframes creceX': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
  animation: `creceX .8s ${SUAVE} both`,
  animationDelay: `${220 + i * 70}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
